import * as React from 'react';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {InContextAlert} from '@spaced-out/ui-design-system/lib/components/InContextAlert';
import {Link} from '@spaced-out/ui-design-system/lib/components/Link';
import {
  FormLabelSmall,
  SubTitleMedium,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Textarea} from '@spaced-out/ui-design-system/lib/components/Textarea';
import {Controller, useFieldArray, useFormContext} from 'react-hook-form';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import type {SlotRequestLimits} from './request-more-availability.utils';

import css from './request-more-availability-form.css';


type RequestDetailsFieldsProps = {
  slotRequestLimits?: SlotRequestLimits | null;
  minDate?: string;
  maxDate?: string;
};

export const RequestDetailsFields = ({
  slotRequestLimits = null,
  minDate,
  maxDate,
}: RequestDetailsFieldsProps) => {
  const {t} = useLabelTranslation();
  const {formState, register, control} = useFormContext();
  const {fields, append, remove} = useFieldArray({
    control,
    name: 'requestedSlots',
  });

  // zodResolver puts the banner message at .root when per-item errors also exist, and at .message when alone.
  const requestedSlotErrors = formState.errors.requestedSlots as
    | {
        root?: {message?: string};
        message?: string;
        [index: number]: {
          date?: {message?: string};
          start_time?: {message?: string};
          end_time?: {message?: string};
          overlap?: {message?: string};
        };
      }
    | undefined;

  const rootErrorMessage =
    requestedSlotErrors?.root?.message ?? requestedSlotErrors?.message ?? '';

  if (!slotRequestLimits) {
    return null;
  }

  const {hours, days} = slotRequestLimits;

  return (
    <>
      <SubTitleMedium className={css.proposeTimeSlotsTitle}>
        {t('Propose_Time_Slots', 'Propose Time Slots')}
      </SubTitleMedium>
      <InContextAlert leftIconName="circle-info" semantic="information">
        {t(
          'Propose_slots_requirement_banner',
          `Please select at least ${hours} total hours of availability, spread across at least ${days} different days, to continue.`,
        )}
      </InContextAlert>
      {rootErrorMessage
        ?.split('\n')
        .filter(Boolean)
        .map((msg) => (
          <InContextAlert
            key={msg}
            leftIconName="shield-exclamation"
            semantic="danger"
          >
            {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
            {msg}
          </InContextAlert>
        ))}
      <div className={css.requestSlotsContainer}>
        {fields.map((field, index) => {
          const isOverlap = Boolean(
            requestedSlotErrors?.[index]?.overlap?.message,
          );
          return (
            <div key={field.id}>
              <div className={css.requestSlotRow}>
                <Controller
                  control={control}
                  name={`requestedSlots.${index}.date` as const}
                  render={({field: controllerField}) => (
                    <Input
                      size="small"
                      type="date"
                      label={`${index + 1}. ${t('Date', 'Date')}`}
                      value={(controllerField.value as string) || ''}
                      onChange={controllerField.onChange}
                      onBlur={controllerField.onBlur}
                      name={controllerField.name}
                      min={minDate}
                      max={maxDate}
                      error={Boolean(
                        requestedSlotErrors?.[index]?.date?.message,
                      )}
                      errorText={
                        requestedSlotErrors?.[index]?.date?.message as string
                      }
                      classNames={{box: css.dateInput}}
                    />
                  )}
                />
                <FormLabelSmall>{t('Time_frame', 'Time frame')}</FormLabelSmall>
                <div className={css.timeContainer}>
                  <Controller
                    control={control}
                    name={`requestedSlots.${index}.start_time` as const}
                    render={({field: controllerField}) => (
                      <Input
                        size="small"
                        type="time"
                        value={(controllerField.value as string) || ''}
                        onChange={controllerField.onChange}
                        onBlur={controllerField.onBlur}
                        name={controllerField.name}
                        error={Boolean(
                          requestedSlotErrors?.[index]?.start_time?.message,
                        )}
                        errorText={
                          requestedSlotErrors?.[index]?.start_time
                            ?.message as string
                        }
                        classNames={{box: css.timeInput}}
                      />
                    )}
                  />
                  <span className={css.timeSeparator}>-</span>
                  <Controller
                    control={control}
                    name={`requestedSlots.${index}.end_time` as const}
                    render={({field: controllerField}) => (
                      <Input
                        size="small"
                        type="time"
                        label=""
                        value={(controllerField.value as string) || ''}
                        onChange={controllerField.onChange}
                        onBlur={controllerField.onBlur}
                        name={controllerField.name}
                        error={Boolean(
                          requestedSlotErrors?.[index]?.end_time?.message,
                        )}
                        errorText={
                          requestedSlotErrors?.[index]?.end_time
                            ?.message as string
                        }
                        classNames={{box: css.timeInput}}
                      />
                    )}
                  />
                </div>
                <Link
                  underline={false}
                  color="secondary"
                  iconLeftSize="small"
                  iconLeftName="circle-minus"
                  onClick={() => remove(index)}
                  disabled={fields.length === 1}
                >
                  {`${t('Delete', 'Delete')}`}
                </Link>
                {isOverlap && (
                  <InContextAlert leftIconName="circle-info" semantic="warning">
                    {t(
                      'This_time_slot_is_available_please_select_it_to_schedule_your_meeting',
                      'This time slot is available; please select it to schedule your meeting.',
                    )}
                  </InContextAlert>
                )}
              </div>
            </div>
          );
        })}
        <Link
          underline={false}
          color="clickable"
          iconLeftSize="small"
          iconLeftName="circle-plus"
          onClick={() => {
            append({date: '', start_time: '', end_time: ''});
          }}
        >
          {`${t('Add_another', 'Add another')}`}
        </Link>
      </div>
      <Textarea
        required
        label={t('Add_Comments', 'Add Comments')}
        placeholder={t('Write_here', 'Write here')}
        error={Boolean(formState.errors.requestReason?.message)}
        errorText={formState.errors.requestReason?.message as string}
        helperText={t(
          'Reason_must_be_between_5_and_150_characters',
          'Reason must be between 5 and 150 characters.',
        )}
        {...register('requestReason')}
      />
    </>
  );
};
